import {
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from '@mantine/core';

/**
 * Mantine theme derived from the mockup's tokens.
 *
 * Mantine needs ten-step tuples; the mockup only defines one accent, so the ramp is
 * generated in oklch around it — same hue and chroma, lightness stepped — which keeps the
 * mockup's exact accent at index 6 (the shade Mantine uses for filled buttons).
 */
function oklchRamp(chroma: number, hue: number, lightness: number[]): MantineColorsTuple {
  return lightness.map((l) => `oklch(${l.toFixed(2)} ${chroma} ${hue})`) as unknown as MantineColorsTuple;
}

const mint = oklchRamp(0.13, 168, [0.96, 0.92, 0.88, 0.85, 0.83, 0.81, 0.8, 0.72, 0.63, 0.55]);
const amber = oklchRamp(0.14, 85, [0.96, 0.92, 0.89, 0.86, 0.84, 0.82, 0.8, 0.72, 0.63, 0.55]);
const rose = oklchRamp(0.18, 25, [0.94, 0.9, 0.86, 0.82, 0.78, 0.74, 0.7, 0.63, 0.56, 0.48]);
const azure = oklchRamp(0.12, 240, [0.95, 0.91, 0.87, 0.83, 0.8, 0.77, 0.74, 0.66, 0.58, 0.5]);

/** Neutral ramp taken from the mockup's `--t*` and surface tokens. */
const graphite: MantineColorsTuple = [
  '#f2f5f3',
  '#e6e9e7',
  '#c9d0cb',
  '#a9b1ab',
  '#7e867f',
  '#5f6763',
  '#2f3633',
  '#1e2220',
  '#141817',
  '#0b0d0c',
];

export const theme = createTheme({
  /**
   * Анімації поступаються системному «менше руху» — і це не лише про доступність.
   *
   * Mantine за замовчуванням ставить `respectReducedMotion: false`, тобто ігнорує
   * `prefers-reduced-motion` навіть тоді, коли користувач його попросив. Крім самої поваги до
   * налаштування, від прапорця залежить `useTransition`: при ненульовій тривалості він
   * ставить `setTimeout`, який у тестах переживає знесення jsdom і падає з
   * `window is not defined` — «unhandled error» у прогоні, де всі тести зелені.
   *
   * Половинчастого лікування тут немає: `env="test"` на провайдері цього таймера не чіпає, а
   * сам по собі стаб `matchMedia` без цього прапорця нічого не вимикає. Тому обидві половини
   * тримає `reduced-motion.test.ts`.
   */
  respectReducedMotion: true,
  primaryColor: 'mint',
  primaryShade: { light: 9, dark: 6 },
  colors: { mint, amber, rose, azure, graphite },
  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
  fontFamilyMonospace: "'IBM Plex Mono', ui-monospace, monospace",
  headings: { fontFamily: "'IBM Plex Sans', system-ui, sans-serif", fontWeight: '600' },
  defaultRadius: 'md',
  radius: { xs: '4px', sm: '6px', md: '7px', lg: '10px', xl: '16px' },
  /**
   * Mantine's own ladder, pointed at the same CSS variables as everything else
   * (`styles/tokens.css` → «Метрика»). Duplicating the numbers here is how the two
   * systems drifted last time: Mantine said 12.5px while a hand-written class said 13.
   */
  fontSizes: {
    xs: 'var(--fs-micro)',
    sm: 'var(--fs-small)',
    md: 'var(--fs-cell)',
    lg: 'var(--fs-base)',
    xl: 'var(--fs-lg)',
  },
  spacing: {
    xs: 'var(--sp-2)',
    sm: 'var(--sp-3)',
    md: 'var(--sp-4)',
    lg: 'var(--sp-5)',
    xl: 'var(--sp-6)',
  },
  /**
   * A dense internal CRM, but not as dense as the mockup: the client asked for the
   * mockup's rhythm one comfort step up (14.08). `size="sm"` lands Mantine's controls
   * on the 34px `--ctl-h` the token block declares; `xs` was the mockup's own 28px and
   * is now reserved for inline row actions.
   */
  components: {
    Table: { defaultProps: { verticalSpacing: 9, horizontalSpacing: 15, fz: 'md' } },
    Button: { defaultProps: { size: 'sm' } },
    TextInput: { defaultProps: { size: 'sm' } },
    PasswordInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    MultiSelect: { defaultProps: { size: 'sm' } },
    NumberInput: { defaultProps: { size: 'sm' } },
    Textarea: { defaultProps: { size: 'sm' } },
    Checkbox: { defaultProps: { size: 'sm' } },
    Switch: { defaultProps: { size: 'sm' } },
    Badge: { defaultProps: { size: 'sm', radius: 'sm' } },
  },
});

/**
 * Bridges the mockup's raw tokens into Mantine's variable namespace so both systems agree
 * on background, borders and text colour without duplicating values.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--mantine-font-family': "'IBM Plex Sans', system-ui, sans-serif",
  },
  light: {
    '--mantine-color-body': 'var(--bg)',
    '--mantine-color-text': 'var(--t1)',
    '--mantine-color-dimmed': 'var(--t8)',
    '--mantine-color-default': 'var(--card)',
    '--mantine-color-default-border': 'var(--line2)',
    '--mantine-color-default-hover': 'var(--card-hover)',
    '--mantine-color-anchor': 'var(--g-txt)',
    '--mantine-color-placeholder': 'var(--t9)',
  },
  dark: {
    '--mantine-color-body': 'var(--bg)',
    '--mantine-color-text': 'var(--t1)',
    '--mantine-color-dimmed': 'var(--t8)',
    '--mantine-color-default': 'var(--card)',
    '--mantine-color-default-border': 'var(--line2)',
    '--mantine-color-default-hover': 'var(--card-hover)',
    '--mantine-color-anchor': 'var(--g-txt)',
    '--mantine-color-placeholder': 'var(--t9)',
  },
});

/*
 * The sidebar width used to live here as a JS constant and be applied as an inline style.
 * It is now `--sidebar-w` in `styles/tokens.css`, because the 768–1279 breakpoint has to
 * swap it — and a constant baked into a `style` attribute cannot participate in a media
 * query. Nothing in TypeScript needs the number any more; `theme.test.ts` asserts the
 * token instead, so the mockup's 238px is still pinned by a test.
 */
